/**
 * Platform-admin Notifications & Companies panel — cross-tenant visibility for our own ops staff.
 * Never tenant-facing, never surfaced to any customer org.
 *
 * Same authorisation shape as `platform.contract.ts` (see that file's header):
 * `authenticatePlatformAdmin` is the real gate, not a tenant permission, so every route here is
 * `routeMeta(null, { ai: 'hidden' })` — "signed-in is enough" at the contract layer, restricted to
 * a platform admin's session by the middleware, and kept out of the AI copilot's tool list the
 * same way the rest of platform.contract.ts is (an assistant must never be handed a cross-tenant
 * notification log as a tool).
 *
 * Two reads, one per sidebar section of the panel:
 *   - `listNotifications` — every notification delivery on every channel, across companies, with
 *     the filters the panel offers (company, channel, status, date range, search).
 *   - `listCompanies`     — every company that signed up or was added, with its admin, plan state,
 *     size and last sign-in.
 *
 * WHO RECEIVED IT: the notification list carries the recipient's name and email (and phone for
 * SMS/WhatsApp), because "did the offer letter reach the person, and at which address" is exactly
 * what ops staff open this panel to answer. It is the platform-admin session that makes that
 * acceptable — these routes are unreachable from any tenant session — and the message BODY is
 * deliberately never returned: a payslip or salary notice must not be readable here.
 *
 * DAYS: the date range filters are calendar days in `PLATFORM_REPORTING_TIMEZONE`, not UTC and not
 * the server's zone (CLAUDE.md invariant 3). Instants on the wire stay UTC.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { NOTIFICATION_CHANNELS } from '../enums.js';
import { dateOnlySchema, errorBodySchema, instantSchema, orgIdSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** The zone platform staff read "today" and "yesterday" in. Our ops team works in India. */
export const PLATFORM_REPORTING_TIMEZONE = 'Asia/Kolkata';

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

/** `NotificationDelivery.status` — see the column comment in schema.prisma. */
export const PLATFORM_DELIVERY_STATUSES = [
  'queued',
  'sent',
  'failed',
  'suppressed',
  'skipped',
] as const;
export const platformDeliveryStatusSchema = z.enum(PLATFORM_DELIVERY_STATUSES);
export type PlatformDeliveryStatus = z.infer<typeof platformDeliveryStatusSchema>;

export const platformNotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);

/** One `NotificationDelivery` row: one notification, on one channel, to one recipient. */
export const platformNotificationRowSchema = z.object({
  id: z.string().uuid(),
  orgId: orgIdSchema,
  orgName: z.string(),
  queuedAt: instantSchema,
  sentAt: instantSchema.nullable(),
  failedAt: instantSchema.nullable(),
  eventKey: z.string(),
  /** The catalogue's human label for `eventKey`; a humanised key for an event it no longer lists. */
  eventDisplayName: z.string(),
  module: z.string(),
  channel: z.string(),
  status: platformDeliveryStatusSchema,
  /** Why a delivery was suppressed, skipped or failed. Null for a clean send. */
  reason: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  /** `person` is an employee; `external` is someone with no account (a candidate). */
  recipientKind: z.enum(['person', 'external']),
  recipientName: z.string().nullable(),
  recipientEmail: z.string().nullable(),
  /** Only filled for SMS and WhatsApp, where the phone number is the address that was used. */
  recipientPhone: z.string().nullable(),
});
export type PlatformNotificationRow = z.infer<typeof platformNotificationRowSchema>;

/** Delivery counts by status for the same filters as the list, ignoring the status filter. */
export const platformNotificationSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  suppressed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type PlatformNotificationSummary = z.infer<typeof platformNotificationSummarySchema>;

export const platformNotificationsQuerySchema = z
  .object({
    orgId: orgIdSchema.optional(),
    channel: platformNotificationChannelSchema.optional(),
    status: platformDeliveryStatusSchema.optional(),
    /** Inclusive calendar days in `PLATFORM_REPORTING_TIMEZONE`. Either end may be omitted. */
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    /** Matches company name, recipient name or email, and event (key or label). */
    q: z.string().trim().min(1).max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .refine((query) => !query.from || !query.to || query.from <= query.to, {
    message: '`from` must not be after `to`',
    path: ['from'],
  });
export type PlatformNotificationsQuery = z.infer<typeof platformNotificationsQuerySchema>;

export const platformNotificationsResponseSchema = z.object({
  items: z.array(platformNotificationRowSchema),
  /** Rows matching every filter, including status — for the pager. */
  total: z.number().int().nonnegative(),
  summary: platformNotificationSummarySchema,
  timezone: z.string(),
});
export type PlatformNotificationsResponse = z.infer<typeof platformNotificationsResponseSchema>;

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

/**
 * Where a company stands, derived server-side from the organisation and its subscription:
 *   suspended        the organisation (or its subscription) is suspended
 *   active           a paid subscription in good standing
 *   past_due         a paid subscription with an overdue invoice
 *   trialing         inside the signup trial
 *   trial_ended      the trial ran out and the company never bought a plan (read-only)
 *   cancelled        the customer cancelled
 *   no_subscription  no subscription row at all (an older tenant, or one not yet set up)
 */
export const PLATFORM_COMPANY_STATUSES = [
  'active',
  'trialing',
  'trial_ended',
  'past_due',
  'suspended',
  'cancelled',
  'no_subscription',
] as const;
export const platformCompanyStatusSchema = z.enum(PLATFORM_COMPANY_STATUSES);
export type PlatformCompanyStatus = z.infer<typeof platformCompanyStatusSchema>;

export const platformCompanyRowSchema = z.object({
  orgId: orgIdSchema,
  name: z.string(),
  /** The company's workspace name — it signs in at `<slug>.<our domain>`. */
  slug: z.string(),
  logoUrl: z.string().nullable(),
  status: platformCompanyStatusSchema,
  planName: z.string().nullable(),
  /** End of the signup trial, when there was one. */
  trialEndsAt: instantSchema.nullable(),
  /** The company's first Global Admin. Null if nobody holds that role. */
  adminName: z.string().nullable(),
  adminEmail: z.string().nullable(),
  /** Active employments. */
  employees: z.number().int().nonnegative(),
  /** ISO 3166-1 alpha-2 of the first active legal entity. Null until one is created. */
  country: z.string().nullable(),
  joinedAt: instantSchema,
  /** The latest sign-in by any real user of the company. Null if nobody has signed in. */
  lastLoginAt: instantSchema.nullable(),
});
export type PlatformCompanyRow = z.infer<typeof platformCompanyRowSchema>;

/** Counts across ALL companies — they do not move with the list's filters. */
export const platformCompanyStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  trialing: z.number().int().nonnegative(),
  trialEnded: z.number().int().nonnegative(),
  /** Past due, suspended or cancelled — companies that need a look. */
  needsAttention: z.number().int().nonnegative(),
  /** Joined in the last 30 days. */
  joinedLast30Days: z.number().int().nonnegative(),
  employees: z.number().int().nonnegative(),
});
export type PlatformCompanyStats = z.infer<typeof platformCompanyStatsSchema>;

export const platformCompaniesQuerySchema = z.object({
  status: platformCompanyStatusSchema.optional(),
  /** Only companies that joined within this many days. */
  joinedWithinDays: z.coerce.number().int().min(1).max(3650).optional(),
  /** Matches company name, workspace name, admin name or admin email. */
  q: z.string().trim().min(1).max(100).optional(),
});
export type PlatformCompaniesQuery = z.infer<typeof platformCompaniesQuerySchema>;

export const platformCompaniesResponseSchema = z.object({
  items: z.array(platformCompanyRowSchema),
  stats: platformCompanyStatsSchema,
});
export type PlatformCompaniesResponse = z.infer<typeof platformCompaniesResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const errors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  422: errorBodySchema,
  500: errorBodySchema,
};

const meta = routeMeta(null, { ai: 'hidden' });

export const platformNotificationsContract = c.router(
  {
    listNotifications: {
      method: 'GET',
      path: '/notifications',
      query: platformNotificationsQuerySchema,
      responses: { 200: platformNotificationsResponseSchema, ...errors },
      summary:
        'Every notification delivery across companies and channels, newest first, with status counts',
      metadata: meta,
    },

    listCompanies: {
      method: 'GET',
      path: '/companies',
      query: platformCompaniesQuerySchema,
      responses: { 200: platformCompaniesResponseSchema, ...errors },
      summary: 'Every company on the platform with its admin, plan state, size and last sign-in',
      metadata: meta,
    },
  },
  { pathPrefix: '/platform' },
);

export type PlatformNotificationsContract = typeof platformNotificationsContract;
